import { Menu } from '@mantine/core';
import React, { ReactNode, useEffect, useEffectEvent, useRef } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';

type Options = {
  useExactClickPosition?: boolean;
  excludedTargets?: HTMLElement[];
};

export type ContentContext = {
  target?: HTMLElement | null;
  setExcludedTargets?: ContextMenuState['setExcludedTargets'];
  close?: () => void;
};

interface ContextMenuState {
  opened: boolean;
  target: HTMLElement;
  x: number;
  y: number;
  useExactClickPosition: boolean;
  excludedTargets: HTMLElement[];
  setExcludedTargets: (targets: HTMLElement[]) => void;
  content: ReactNode | ((ctx: ContentContext) => ReactNode) | null;
  open: (
    e: MouseEvent | React.MouseEvent,
    content: ContextMenuState['content'],
    opts?: Options,
  ) => void;
  close: () => void;
  _timeoutId: number | null;
}

export const useContextMenuStore = create<ContextMenuState>((set, get) => ({
  opened: false,
  x: 0,
  y: 0,
  target: null,
  useExactClickPosition: false,
  excludedTargets: [],
  content: null,
  _timeoutId: null,
  setExcludedTargets: (targets: HTMLElement[]) =>
    set({ excludedTargets: targets }),
  open: (_e, content, opts) => {
    const e = (_e as React.MouseEvent)?.nativeEvent || _e;

    const currentTimeoutId = get()._timeoutId;
    if (currentTimeoutId !== null) {
      clearTimeout(currentTimeoutId);
    }

    get().close();
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const timeoutId = setTimeout(() => {
      set({
        target: e.target as HTMLElement,
        opened: true,
        x: e?.clientX ?? 0,
        y: e?.clientY ?? 0,
        content,
        useExactClickPosition: opts?.useExactClickPosition ?? false,
        excludedTargets: opts?.excludedTargets ?? [],
        _timeoutId: null,
      });
    }, 0) as unknown as number;

    set({ _timeoutId: timeoutId });
  },
  close: () => {
    const timeoutId = get()._timeoutId;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    set({
      opened: false,
      content: null,
      target: null,
      x: 0,
      y: 0,
      excludedTargets: [],
      _timeoutId: null,
    });
  },
}));

export const ContextMenu = () => {
  const { opened, x, y, content, close, excludedTargets, setExcludedTargets } =
    useContextMenuStore();
  const menuRef = useRef<HTMLDivElement>(null);

  const stableSetExcludedTargets = useEffectEvent(setExcludedTargets);

  const onClose = (e?: MouseEvent) => {
    const _target = e?.target;
    if (
      menuRef.current?.contains(_target as Node) ||
      excludedTargets.some((x) => x.contains(_target as Node))
    ) {
      return;
    }

    close();
  };

  const handleClose = useEffectEvent(onClose);

  useEffect(() => {
    if (!opened) return;
    document.addEventListener('click', handleClose, true);
    document.addEventListener('wheel', handleClose, true);
    return () => {
      document.removeEventListener('click', handleClose, true);
      document.removeEventListener('wheel', handleClose, true);
    };
  }, [opened, handleClose]);

  useEffect(() => {
    return () => {
      const timeoutId = useContextMenuStore.getState()._timeoutId;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const renderContent = ({ close }: { close: () => void }) => {
    if (typeof content === 'function') {
      return (content as (ctx: ContentContext) => ReactNode)({
        target: menuRef.current,
        setExcludedTargets: stableSetExcludedTargets,
        close,
      });
    }
    return content;
  };

  return (
    <Menu opened={opened}>
      {/* A point has no size for ResizeObserver to detect its movement.
          Replace the reference so Mantine remeasures even during a close/open transition. */}
      <Menu.Target key={`${x}:${y}`}>
        <div
          data-testid='context-menu-anchor'
          style={{
            width: 0,
            height: 0,
            position: 'fixed',
            left: x,
            top: y,
          }}
        />
      </Menu.Target>
      <Menu.Dropdown
        ref={menuRef}
        data-testid='context-menu-dropdown'
        onWheel={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {renderContent({ close })}
      </Menu.Dropdown>
    </Menu>
  );
};

export const useContextMenu = () => {
  const open = useContextMenuStore((s) => s.open);

  return (content: ContextMenuState['content'], opts?: Options) =>
    (e: MouseEvent | React.MouseEvent) =>
      open((e as React.MouseEvent)?.nativeEvent || e, content, opts);
};

export const useAttachContextMenu = (
  target: React.RefObject<HTMLElement | null> | HTMLElement | null | undefined,
  getContent: (ctx: ContentContext) => ReactNode,
  opts?: Options,
) => {
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });

  useEffect(() => {
    const el =
      target && 'current' in target ? target.current : (target as HTMLElement);
    if (!el) return;

    const handler = (e: MouseEvent) => {
      useContextMenuStore
        .getState()
        .open(e, (ctx) => getContent(ctx), optsRef.current);
    };

    el.addEventListener('contextmenu', handler);
    return () => el.removeEventListener('contextmenu', handler);
  }, [target, getContent]);
};